import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, ArrowRight, Landmark, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import api from "../../lib/api";

const aud = (n) => `A$${Number(n).toFixed(2)}`;

const BANK = {
  name: "A and N Group Pty Ltd",
  bsb: "013443",
  acc: "212462215",
  ref: "Your name + order email",
};

export default function CartDrawer() {
  const { items, open, setOpen, remove, setQty, total, clear } = useCart();
  const [step, setStep] = useState("cart"); // cart | details | done
  const [method, setMethod] = useState("bank_transfer");
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.customer_name || !form.email) { toast.error("Please add your name and email"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        payment_method: method,
        items: items.map((i) => ({ ref_id: i.ref_id, name: i.name, kind: i.kind, price: i.price, qty: i.qty })),
      };
      const { data } = await api.post("/orders", payload);
      if (method === "paypal") {
        toast.success("Redirecting to PayPal…");
        payWithPayPal(items.map((i) => ({ name: i.name, price: i.price, qty: i.qty })));
        return;
      }
      setOrderId(data.id);
      setStep("done");
      clear();
      toast.success("Order placed! Check your email for confirmation.");
    } catch (e) {
      toast.error("Could not place order. Please try again.");
    } finally { setSubmitting(false); }
  };

  const close = () => { setOpen(false); setTimeout(() => setStep("cart"), 300); };

  const payWithPayPal = (payItems) => {
    const business = process.env.REACT_APP_PAYPAL_BUSINESS;
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://www.paypal.com/cgi-bin/webscr";
    const addField = (name, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField("cmd", "_cart");
    addField("upload", "1");
    addField("business", business);
    addField("currency_code", "AUD");
    payItems.forEach((it, i) => {
      const n = i + 1;
      addField(`item_name_${n}`, it.name);
      addField(`amount_${n}`, Number(it.price).toFixed(2));
      addField(`quantity_${n}`, String(it.qty));
    });
    addField("return", `${window.location.origin}/?paid=1`);
    addField("cancel_return", `${window.location.origin}/`);
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} data-testid="cart-overlay" />
          <motion.aside
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white flex flex-col border-l border-neutral-200"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-200">
              <span className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500">
                {step === "cart" ? "Your Cart" : step === "details" ? "Checkout" : "Confirmed"}
              </span>
              <button onClick={close} data-testid="cart-close-btn" className="p-1 hover:rotate-90 transition-transform duration-300"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {step === "cart" && (
                <div className="p-6 space-y-4">
                  {items.length === 0 && <p className="text-neutral-500 py-16 text-center">Your cart is empty.</p>}
                  {items.map((i) => (
                    <div key={i.ref_id} className="flex gap-4 border border-neutral-200 p-3" data-testid={`cart-item-${i.ref_id}`}>
                      {i.image && <img src={i.image} alt={i.name} className="w-16 h-16 object-cover" />}
                      <div className="flex-1">
                        <p className="font-display font-bold leading-tight">{i.name}</p>
                        <p className="text-xs text-neutral-500 mb-2">{aud(i.price)} · {i.kind === "plan" ? "/mo" : "one-off"}</p>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setQty(i.ref_id, i.qty - 1)} className="border border-neutral-300 p-1"><Minus size={12} /></button>
                          <span className="font-mono-x text-sm w-5 text-center">{i.qty}</span>
                          <button onClick={() => setQty(i.ref_id, i.qty + 1)} className="border border-neutral-300 p-1"><Plus size={12} /></button>
                          <button onClick={() => remove(i.ref_id)} className="ml-auto text-xs text-neutral-400 hover:text-red-500">Remove</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step === "details" && (
                <div className="p-6 space-y-4">
                  {[["customer_name", "Full name"], ["email", "Email address"], ["phone", "Phone"], ["address", "Delivery address"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-neutral-500">{label}</label>
                      <input
                        data-testid={`checkout-${k}`}
                        value={form[k]} onChange={(e) => upd(k, e.target.value)}
                        className="w-full border border-neutral-300 px-3 py-2 mt-1 focus:border-[#FF5722] outline-none"
                      />
                    </div>
                  ))}
                  <div className="pt-2">
                    <p className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-2">Payment method</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setMethod("bank_transfer")} data-testid="pay-bank-btn"
                        className={`border p-3 text-sm flex items-center gap-2 ${method === "bank_transfer" ? "border-[#FF5722] bg-blue-50" : "border-neutral-300"}`}>
                        <Landmark size={16} /> Bank Transfer
                      </button>
                      <button onClick={() => setMethod("paypal")} data-testid="pay-paypal-btn"
                        className={`border p-3 text-sm flex items-center gap-2 ${method === "paypal" ? "border-[#FF5722] bg-blue-50" : "border-neutral-300"}`}>
                        <span className="font-bold text-[#003087]">Pay</span><span className="font-bold text-[#009cde]">Pal</span>
                      </button>
                    </div>
                    {method === "bank_transfer" && (
                      <div className="mt-3 bg-neutral-50 border border-neutral-200 p-3 text-sm font-mono-x space-y-1">
                        <p>{BANK.name}</p>
                        <p>BSB: {BANK.bsb}</p>
                        <p>ACC: {BANK.acc}</p>
                        <p className="text-neutral-500">Ref: {BANK.ref}</p>
                      </div>
                    )}
                    {method === "paypal" && (
                      <p className="mt-3 text-xs text-neutral-500">You'll be redirected to PayPal to pay securely to nanakgroceries@gmail.com. Your order is saved as pending until payment is confirmed.</p>
                    )}
                  </div>
                </div>
              )}

              {step === "done" && (
                <div className="p-8 text-center flex flex-col items-center gap-4 mt-10">
                  <CheckCircle2 size={56} className="text-[#00C473]" />
                  <h3 className="font-display text-2xl font-bold">Order received</h3>
                  <p className="text-neutral-500 text-sm">Order ref <span className="font-mono-x text-neutral-900">#{orderId.slice(-6)}</span>. We'll email you payment & delivery details shortly.</p>
                  <button onClick={close} className="mt-2 bg-[#0F172A] text-white px-6 py-3 text-sm font-medium" data-testid="done-close-btn">Keep browsing</button>
                </div>
              )}
            </div>

            {step !== "done" && items.length > 0 && (
              <div className="border-t border-neutral-200 p-6 space-y-4">
                <div className="flex justify-between font-display text-lg font-bold">
                  <span>Total</span><span>{aud(total)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-500"><ShieldCheck size={14} className="text-[#00C473]" /> Australian warranty · Local support</div>
                {step === "cart" ? (
                  <button onClick={() => setStep("details")} data-testid="cart-checkout-btn"
                    className="w-full bg-[#FF5722] text-white py-3.5 font-medium flex items-center justify-center gap-2 group">
                    Checkout <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting} data-testid="place-order-btn"
                    className="w-full bg-[#0F172A] text-white py-3.5 font-medium disabled:opacity-50">
                    {submitting ? "Placing order…" : "Place order"}
                  </button>
                )}
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
